import React, { useEffect, useMemo, useRef, useState } from "react";
import PitchCurveAudioPanel from "./PitchCurveAudioPanel";

function cx(...classes) {
  return classes.filter(Boolean).join(" ");
}

function resolveAudioUrl(audioUrl) {
  if (!audioUrl) return "";
  if (/^(https?:|blob:)/i.test(audioUrl)) return audioUrl;
  return `${process.env.REACT_APP_USER_API_URL}${audioUrl}`;
}

function isPrivateAudioRoute(audioUrl) {
  return typeof audioUrl === "string" && audioUrl.includes("/audio/private/");
}

async function fetchProtectedAudioBlobUrl(audioUrl) {
  const token = localStorage.getItem("token");

  if (!audioUrl || !token) return null;

  const absoluteUrl = resolveAudioUrl(audioUrl);

  const response = await fetch(absoluteUrl, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error(`Protected audio fetch failed: ${response.status}`);
  }

  const blob = await response.blob();

  return URL.createObjectURL(blob);
}

function getOwner(recording) {
  return (
    recording?.owner?.username ||
    recording?.owner_username ||
    recording?.username ||
    "User"
  );
}

function getRecordingId(recording) {
  return recording?.recording_id || recording?.id || recording?._id || "";
}

function getExercisePath(recording) {
  const recordingId = getRecordingId(recording);
  if (!recordingId) return "";

  return `/exercise/${encodeURIComponent(String(recordingId))}`;
}

function getExerciseUrl(recording) {
  const path = getExercisePath(recording);
  if (!path) return "";

  if (typeof window === "undefined") return path;

  return `${window.location.origin}${path}`;
}

function sortSegments(items) {
  return [...(items || [])].sort((a, b) => Number(a.index || 0) - Number(b.index || 0));
}

function formatSegmentLabel(item, fallbackIndex) {
  const value = item?.index || fallbackIndex + 1;
  return `Segment ${value}`;
}

function normalizeWord(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[.,!?;:()[\]{}"“”'`]+$/g, "");
}

function getWordScoreColor(score, errorType) {
  if (errorType === "Omission") return "bg-gray-400";
  if (typeof score !== "number") return "bg-gray-400";
  if (score >= 80) return "bg-green-500";
  if (score >= 40) return "bg-yellow-500";
  return "bg-red-500";
}

function getPhonemeDotColor(score) {
  if (typeof score !== "number") return "bg-gray-400";
  if (score >= 80) return "bg-green-500";
  if (score >= 40) return "bg-yellow-500";
  return "bg-red-500";
}

function getWordAssessmentClasses(score, errorType) {
  if (errorType === "Omission") return "bg-gray-50 border-gray-300 text-gray-800";
  if (typeof score !== "number") return "bg-white border-slate-200 text-slate-800";
  if (score >= 80) return "bg-green-50 border-green-200 text-slate-900";
  if (score >= 40) return "bg-yellow-50 border-yellow-200 text-slate-900";
  return "bg-red-50 border-red-200 text-slate-900";
}

function getAssessmentWords(assessment) {
  return (
    assessment?.NBest?.[0]?.Words ||
    assessment?.pronunciation?.NBest?.[0]?.Words ||
    []
  );
}

function getAssessmentOverall(assessment) {
  return (
    assessment?.NBest?.[0]?.PronunciationAssessment?.AccuracyScore ??
    assessment?.PronunciationAssessment?.AccuracyScore ??
    assessment?.pronunciation?.NBest?.[0]?.PronunciationAssessment?.AccuracyScore ??
    null
  );
}

function buildSegmentAssessmentMap(segmentWords = [], assessmentWords = []) {
  let pointer = 0;

  return segmentWords.map((item) => {
    const segmentText = normalizeWord(item?.text);

    if (!segmentText) return null;

    for (let i = pointer; i < assessmentWords.length; i += 1) {
      if (normalizeWord(assessmentWords[i]?.Word) === segmentText) {
        pointer = i + 1;
        return assessmentWords[i];
      }
    }

    return assessmentWords.find((word) => normalizeWord(word?.Word) === segmentText) || null;
  });
}

function splitTextIntoWords(text) {
  return String(text || "")
    .split(/\s+/)
    .filter(Boolean)
    .map((word, index) => ({
      id: index,
      text: word,
    }));
}

function AssessmentWordDots({ text, assessment, title, tone = "blue" }) {
  const words = useMemo(() => splitTextIntoWords(text), [text]);
  const assessmentWords = useMemo(() => getAssessmentWords(assessment), [assessment]);
  const mappedAssessmentWords = useMemo(
    () => buildSegmentAssessmentMap(words, assessmentWords),
    [words, assessmentWords],
  );
  const overall = getAssessmentOverall(assessment);

  const toneClass = tone === "green" ? "text-green-700" : "text-blue-700";

  if (!assessment) {
    return (
      <div className="rounded-[24px] border border-dashed border-slate-200 bg-slate-50 px-5 py-8 text-center text-sm font-semibold text-slate-500">
        Record once to show AI word and phoneme feedback.
      </div>
    );
  }

  return (
    <section className="rounded-[24px] border border-slate-200 bg-white p-4 shadow-sm">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className={`text-xs font-black uppercase tracking-[0.18em] ${toneClass}`}>
            {title}
          </p>
          <h3 className="mt-1 text-lg font-black text-slate-950">
            AI assessment
          </h3>
        </div>

        {typeof overall === "number" && (
          <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-black text-slate-700">
            Overall {overall}
          </span>
        )}
      </div>

      <div className="flex flex-wrap gap-2">
        {words.map((word, index) => {
          const matchedAssessment = mappedAssessmentWords[index];
          const pronunciation = matchedAssessment?.PronunciationAssessment;
          const phonemes = matchedAssessment?.Phonemes || [];
          const score = pronunciation?.AccuracyScore;
          const errorType = pronunciation?.ErrorType;
          const assessmentClass = getWordAssessmentClasses(score, errorType);

          return (
            <span
              key={`${word.text}-${index}`}
              className={`flex min-w-[62px] flex-col items-center gap-1 rounded-xl border px-3 py-2 text-center text-sm font-bold shadow-sm ${assessmentClass}`}
              title={
                matchedAssessment
                  ? `${matchedAssessment?.Word || word.text}${typeof score === "number" ? ` • Score: ${score}` : ""}${errorType ? ` • ${errorType}` : ""}`
                  : word.text
              }
            >
              <span className="leading-tight text-slate-900">
                {word.text}
              </span>

              <span className="flex h-2.5 items-center justify-center gap-1">
                {phonemes.length > 0 ? (
                  phonemes.map((phoneme, phonemeIndex) => (
                    <span
                      key={`${phoneme?.Phoneme || "ph"}-${phonemeIndex}`}
                      className={`h-2 w-2 rounded-full ${getPhonemeDotColor(
                        phoneme?.PronunciationAssessment?.AccuracyScore,
                      )} ring-1 ring-white shadow-sm`}
                      title={`${phoneme?.Phoneme || "phoneme"}: ${
                        phoneme?.PronunciationAssessment?.AccuracyScore ?? "N/A"
                      }`}
                    />
                  ))
                ) : matchedAssessment ? (
                  <span
                    className={`h-2.5 w-2.5 rounded-full ${getWordScoreColor(
                      score,
                      errorType,
                    )} ring-1 ring-white shadow-sm`}
                  />
                ) : (
                  <span className="h-2.5 w-2.5 rounded-full bg-gray-300 ring-1 ring-white shadow-sm" />
                )}
              </span>
            </span>
          );
        })}
      </div>
    </section>
  );
}

const MODES = [
  {
    id: "shadowing",
    label: "Shadowing",
    subtitle: "Cue · record · reference plays.",
  },
  {
    id: "echoing",
    label: "Echoing",
    subtitle: "Listen · cue · record.",
  },
  {
    id: "manual",
    label: "Manual",
    subtitle: "Record and play yourself.",
  },
];

const SHADOWING_TIMING = {
  CUE_TOTAL_DURATION: 2200,
  PULSE_1_TIME: 200,
  PULSE_2_TIME: 800,
  PULSE_3_TIME: 1600,
  CUE_CLEAR_TIME: 600,
  RECORDING_START_OFFSET: -100,
  AUDIO_START_DELAY: 2200,
  POST_AUDIO_BUFFER: 200,
};

const ECHOING_TIMING = {
  PULSE_1_TIME: 200,
  PULSE_2_TIME: 500,
  PULSE_3_TIME: 900,
  CUE_CLEAR_TIME: 200,
  TOTAL_DURATION: 1100,
};

const WORKFLOW_LABELS = {
  idle: "Ready",
  cue: "Get ready",
  playing: "Listen",
  countdown: "Get ready",
  recording: "Recording",
  stopping: "Stopping",
  analyzing: "Analyzing",
  ready: "Ready",
};

function CueDots({ count = 0, active = false, tone = "blue" }) {
  const activeClass = tone === "amber"
    ? "border-amber-300 bg-amber-500 text-white shadow-amber-500/30"
    : "border-blue-300 bg-blue-600 text-white shadow-blue-600/30";

  return (
    <div className="flex items-center gap-2" aria-hidden="true">
      {[1, 2, 3].map((idx) => (
        <span
          key={idx}
          className={`flex h-8 w-8 items-center justify-center rounded-full border text-xs font-black shadow-sm transition ${
            active && count >= idx
              ? `${activeClass} scale-110 shadow-md`
              : "border-slate-200 bg-white text-slate-400"
          }`}
        >
          {idx}
        </span>
      ))}
    </div>
  );
}

function PrimaryActionIcon({
  value = 0,
  tone = "blue",
  active = false,
  recording = false,
  symbol = "▶",
}) {
  const safeValue = Math.max(0, Math.min(100, Number(value) || 0));
  const toneMap = {
    blue: {
      fill: "rgba(255,255,255,0.96)",
      track: "rgba(255,255,255,0.24)",
      inner: "bg-white text-blue-700",
    },
    amber: {
      fill: "rgba(255,255,255,0.96)",
      track: "rgba(255,255,255,0.24)",
      inner: "bg-white text-amber-700",
    },
    red: {
      fill: "rgba(255,255,255,0.96)",
      track: "rgba(255,255,255,0.24)",
      inner: "bg-white text-red-700",
    },
    green: {
      fill: "rgba(255,255,255,0.96)",
      track: "rgba(255,255,255,0.24)",
      inner: "bg-white text-green-700",
    },
    slate: {
      fill: "rgba(255,255,255,0.96)",
      track: "rgba(255,255,255,0.24)",
      inner: "bg-white text-slate-700",
    },
  };

  const toneSet = toneMap[tone] || toneMap.blue;
  const showProgress = active && safeValue > 0;

  return (
    <span className="relative flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-white/20 shadow-inner ring-1 ring-white/35">
      <span
        className="absolute inset-1 rounded-full transition-[background] duration-150"
        style={{
          background: showProgress
            ? `conic-gradient(${toneSet.fill} ${safeValue * 3.6}deg, ${toneSet.track} 0deg)`
            : "rgba(255,255,255,0.24)",
        }}
      />
      <span className="absolute inset-[9px] rounded-full bg-white/20" />
      <span className={`relative flex h-8 w-8 items-center justify-center rounded-full text-sm font-black ${toneSet.inner}`}>
        {recording ? "■" : symbol}
      </span>
    </span>
  );
}


function ProcessingActionCard() {
  return (
    <div className="relative flex h-20 w-full max-w-[420px] items-center justify-center overflow-hidden rounded-[24px] border border-blue-200 bg-gradient-to-r from-blue-50 via-white to-cyan-50 px-8 shadow-[0_18px_45px_-28px_rgba(37,99,235,0.65)] ring-1 ring-blue-100">
      <span className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-blue-500 via-cyan-400 to-indigo-500" aria-hidden="true" />
      <span className="absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(59,130,246,0.12),transparent_42%)]" aria-hidden="true" />

      <span className="relative flex items-center gap-4">
        <span className="relative flex h-12 w-12 items-center justify-center rounded-full bg-white shadow-inner ring-1 ring-blue-100">
          <span className="absolute inset-1 rounded-full border-[3px] border-blue-100" />
          <span className="absolute inset-1 animate-spin rounded-full border-[3px] border-transparent border-t-blue-600 border-r-cyan-400" />
          <span className="relative h-3 w-3 rounded-full bg-blue-600 shadow-sm shadow-blue-500/30" />
        </span>

        <span className="text-left">
          <span className="block text-lg font-black leading-5 text-slate-950">
            Processing recording
          </span>
          <span className="mt-1 block text-xs font-semibold text-slate-500">
            Uploading audio and preparing AI feedback…
          </span>
        </span>
      </span>
    </div>
  );
}

function isTransientUserAudioError(message) {
  return [
    "Could not play your recording.",
    "Could not prepare your recording for playback.",
    "Preparing your recording for playback…",
    "Record your version first.",
  ].includes(message);
}

function getRecorderMimeType() {
  if (typeof MediaRecorder === "undefined") return "";

  const ua = navigator.userAgent || "";
  const isFirefox = ua.includes("Firefox/");
  const isOpera = ua.includes("OPR/") || ua.includes("Opera");
  const isSafari =
    /Safari/i.test(ua) &&
    !/Chrome|Chromium|CriOS|Edg|OPR|Firefox/i.test(ua);

  const candidates = isFirefox
    ? [
        "audio/ogg;codecs=opus",
        "audio/webm;codecs=opus",
        "audio/ogg",
        "audio/webm",
      ]
    : isOpera
      ? [
          "audio/webm;codecs=opus",
          "audio/webm",
          "audio/mp4",
        ]
      : isSafari
        ? [
            "audio/mp4",
            "audio/webm;codecs=opus",
            "audio/webm",
          ]
        : [
            "audio/webm;codecs=opus",
            "audio/webm",
            "audio/mp4",
            "audio/ogg;codecs=opus",
          ];

  for (const type of candidates) {
    try {
      if (MediaRecorder.isTypeSupported?.(type)) return type;
    } catch {
      // keep trying
    }
  }

  return "";
}

function getRecorderOptions() {
  const pickedMimeType = getRecorderMimeType();
  return pickedMimeType ? { mimeType: pickedMimeType } : undefined;
}

function createRecorder(mediaStream) {
  if (typeof MediaRecorder === "undefined") {
    throw new Error("MediaRecorder is not supported in this browser.");
  }

  if (!(mediaStream instanceof MediaStream)) {
    throw new Error("createRecorder expected a MediaStream");
  }

  const options = getRecorderOptions();

  return options
    ? new MediaRecorder(mediaStream, options)
    : new MediaRecorder(mediaStream);
}

function getRecordingExtension(mimeType = "") {
  const value = String(mimeType || "").toLowerCase();

  if (value.includes("mp4") || value.includes("mpeg")) return "mp4";
  if (value.includes("ogg")) return "ogg";
  if (value.includes("wav")) return "wav";
  if (value.includes("webm")) return "webm";

  return "webm";
}

function isLiveMediaStream(mediaStream) {
  return (
    mediaStream instanceof MediaStream &&
    mediaStream.getAudioTracks().some((track) => track.readyState === "live")
  );
}

async function requestMicrophoneStream() {
  if (!navigator.mediaDevices?.getUserMedia) {
    throw new Error("getUserMedia is not supported in this browser.");
  }

  try {
    return await navigator.mediaDevices.getUserMedia({
      audio: {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      },
      video: false,
    });
  } catch {
    return navigator.mediaDevices.getUserMedia({
      audio: true,
      video: false,
    });
  }
}

export default function SharedRecordingExerciseOverlay({ recording, onBack }) {
  const [fullRecording, setFullRecording] = useState(recording);
  const [segments, setSegments] = useState(() => sortSegments(recording?.items || []));
  const [activeIndex, setActiveIndex] = useState(0);
  const [mode, setMode] = useState("shadowing");
  const [loading, setLoading] = useState(false);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [error, setError] = useState("");
  const [narratorIntonationByItem, setNarratorIntonationByItem] = useState({});
  const [userResultByItem, setUserResultByItem] = useState({});
  const [protectedUserAudioUrl, setProtectedUserAudioUrl] = useState(null);
  const [isPreparingUserAudio, setIsPreparingUserAudio] = useState(false);
  const [playingReference, setPlayingReference] = useState(false);
  const [playingUser, setPlayingUser] = useState(false);
  const [recordingNow, setRecordingNow] = useState(false);
  const [recordingStatus, setRecordingStatus] = useState("");
  const [workflowPhase, setWorkflowPhase] = useState("idle");
  const [cuePulseCount, setCuePulseCount] = useState(0);
  const [echoingPulseCount, setEchoingPulseCount] = useState(0);
  const [workflowProgressPercent, setWorkflowProgressPercent] = useState(0);
  const [workflowProgressDurationMs, setWorkflowProgressDurationMs] = useState(300);

  const referenceAudioRef = useRef(null);
  const userAudioRef = useRef(null);
  const protectedUserAudioObjectUrlRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const preparedStreamRef = useRef(null);
  const chunksRef = useRef([]);
  const workflowTimeoutsRef = useRef([]);
  const maxRecordingTimeoutRef = useRef(null);
  const workflowProgressRafRef = useRef(null);

  const fullRecordingOrInitial = fullRecording || recording;
  const recordingId = getRecordingId(fullRecordingOrInitial);
  const exerciseUrl = getExerciseUrl(fullRecordingOrInitial);
  const activeSegment = segments[activeIndex] || null;
  const activeItemId = activeSegment?.item_id || activeSegment?.attempt_id || activeSegment?.audio_id || activeSegment?.index || "";
  const activeKey = String(activeItemId || activeIndex);
  const owner = getOwner(fullRecordingOrInitial);
  const nativeSpeaker = Boolean(fullRecordingOrInitial?.is_native_speaker) || fullRecordingOrInitial?.speaker_label === "native speaker";
  const description = String(fullRecordingOrInitial?.description || "").trim();
  const isFirst = activeIndex <= 0;
  const isLast = activeIndex >= segments.length - 1;
  const narratorPayload = narratorIntonationByItem[activeKey] || null;
  const narratorIntonation = narratorPayload?.narrator_intonation || narratorPayload;
  const userResult = userResultByItem[activeKey] || null;
  const userIntonation = userResult?.user_intonation || userResult?.intonation || null;
  const userPronunciation =
    userResult?.user_pronunciation ||
    userResult?.pronunciation ||
    userIntonation?.pronunciation ||
    null;
  const userAudioUrl = userResult?.audio_url || "";
  const needsProtectedUserAudio = isPrivateAudioRoute(userAudioUrl);
  const playableUserAudioUrl = needsProtectedUserAudio
    ? protectedUserAudioUrl
    : resolveAudioUrl(userAudioUrl);
  const activeMode = MODES.find((item) => item.id === mode) || MODES[0];
  const cueCount = mode === "echoing" ? echoingPulseCount : cuePulseCount;
  const isCueVisible = workflowPhase === "cue" || workflowPhase === "countdown";
  const workflowLabel = WORKFLOW_LABELS[workflowPhase] || WORKFLOW_LABELS.idle;
  const referenceDurationMs = Number.isFinite(Number(referenceAudioRef.current?.duration))
    ? Math.max(1200, Number(referenceAudioRef.current.duration) * 1000)
    : 3000;
  const maxRecordingMs = Math.max(5000, referenceDurationMs * 3);
  const primaryButtonConfig = (() => {
    if (recordingNow || workflowPhase === "recording") {
      return {
        text: "Stop recording",
        tone: "red",
        symbol: "■",
        disabled: false,
      };
    }

    if (analysisLoading || workflowPhase === "analyzing") {
      return {
        text: "Analyzing…",
        tone: "slate",
        symbol: "…",
        disabled: true,
      };
    }

    if (workflowPhase === "playing") {
      return {
        text: "Playing reference…",
        tone: "blue",
        symbol: "▶",
        disabled: true,
      };
    }

    if (workflowPhase === "cue" || workflowPhase === "countdown") {
      return {
        text: "Get ready…",
        tone: "amber",
        symbol: "3",
        disabled: true,
      };
    }

    if (mode === "shadowing") {
      return {
        text: "Shadow & record",
        tone: "amber",
        symbol: "↗",
        disabled: false,
      };
    }

    if (mode === "echoing") {
      return {
        text: "Start echoing",
        tone: "blue",
        symbol: "↻",
        disabled: false,
      };
    }

    return {
      text: "Start recording",
      tone: "green",
      symbol: "●",
      disabled: false,
    };
  })();

  const isProcessingRecording = analysisLoading || workflowPhase === "analyzing";
  const primaryProgressActive = ["cue", "countdown", "playing", "recording"].includes(workflowPhase);
  const primaryButtonSubtitle = primaryProgressActive
    ? `${workflowLabel} • ${Math.round(workflowProgressPercent)}%`
    : activeMode.subtitle;
  const primaryButtonRecording = recordingNow || workflowPhase === "recording";

  const progressPercent = useMemo(() => {
    if (!segments.length) return 0;
    return ((activeIndex + 1) / segments.length) * 100;
  }, [activeIndex, segments.length]);

  const clearWorkflowTimers = () => {
    workflowTimeoutsRef.current.forEach((timeout) => timeout && clearTimeout(timeout));
    workflowTimeoutsRef.current = [];

    if (maxRecordingTimeoutRef.current) {
      clearTimeout(maxRecordingTimeoutRef.current);
      maxRecordingTimeoutRef.current = null;
    }

    if (workflowProgressRafRef.current) {
      cancelAnimationFrame(workflowProgressRafRef.current);
      workflowProgressRafRef.current = null;
    }
  };

  const scheduleWorkflowTimeout = (callback, delay) => {
    const timeout = setTimeout(callback, delay);
    workflowTimeoutsRef.current.push(timeout);
    return timeout;
  };

  const startWorkflowProgress = (durationMs = 1200) => {
    if (workflowProgressRafRef.current) {
      cancelAnimationFrame(workflowProgressRafRef.current);
      workflowProgressRafRef.current = null;
    }

    const total = Math.max(250, Number(durationMs) || 1200);
    const startedAt = performance.now();

    setWorkflowProgressDurationMs(total);
    setWorkflowProgressPercent(0);

    const tick = (now) => {
      const elapsed = now - startedAt;
      const next = Math.min((elapsed / total) * 100, 100);

      setWorkflowProgressPercent(next);

      if (next < 100) {
        workflowProgressRafRef.current = requestAnimationFrame(tick);
      } else {
        workflowProgressRafRef.current = null;
      }
    };

    workflowProgressRafRef.current = requestAnimationFrame(tick);
  };

  const stopWorkflowProgress = () => {
    if (workflowProgressRafRef.current) {
      cancelAnimationFrame(workflowProgressRafRef.current);
      workflowProgressRafRef.current = null;
    }

    setWorkflowProgressDurationMs(180);
    setWorkflowProgressPercent(0);
  };

  const stopMediaStream = (targetStream = mediaStreamRef.current) => {
    if (targetStream) {
      try {
        targetStream.getTracks().forEach((track) => track.stop());
      } catch {
        // ignore cleanup errors
      }
    }

    if (!targetStream || mediaStreamRef.current === targetStream) {
      mediaStreamRef.current = null;
    }

    if (!targetStream || preparedStreamRef.current === targetStream) {
      preparedStreamRef.current = null;
    }
  };

  const prepareMicrophoneStream = async () => {
    if (isLiveMediaStream(preparedStreamRef.current)) {
      return preparedStreamRef.current;
    }

    if (isLiveMediaStream(mediaStreamRef.current)) {
      preparedStreamRef.current = mediaStreamRef.current;
      return mediaStreamRef.current;
    }

    if (typeof MediaRecorder === "undefined") {
      throw new Error("MediaRecorder is not supported in this browser.");
    }

    const nextStream = await requestMicrophoneStream();

    mediaStreamRef.current = nextStream;
    preparedStreamRef.current = nextStream;

    return nextStream;
  };

  const handleMicrophoneFailure = (error) => {
    console.error("Microphone permission or recording failed:", error);
    setError("Microphone permission or recording failed.");
    setRecordingStatus("");
    setWorkflowPhase("idle");
    setRecordingNow(false);
    stopWorkflowProgress();
    stopMediaStream();
  };

  const unlockReferenceAudioForIOS = async () => {
    const audio = referenceAudioRef.current;

    if (!audio || !activeSegment?.audio_url) return;

    const previousMuted = audio.muted;
    const previousVolume = audio.volume;
    const previousTime = Number.isFinite(audio.currentTime) ? audio.currentTime : 0;

    try {
      audio.muted = true;
      audio.volume = 0;

      const playPromise = audio.play();

      if (playPromise && typeof playPromise.then === "function") {
        await playPromise;
      }

      audio.pause();

      try {
        audio.currentTime = previousTime || 0;
      } catch {
        // ignore currentTime reset errors
      }
    } catch {
      // iOS/audio unlock is best-effort only
    } finally {
      audio.muted = previousMuted;
      audio.volume = previousVolume;
    }
  };

  const resetWorkflowVisuals = () => {
    clearWorkflowTimers();
    setCuePulseCount(0);
    setEchoingPulseCount(0);
    setWorkflowPhase("idle");
    stopWorkflowProgress();
  };

  const stopReferenceAudio = () => {
    const audio = referenceAudioRef.current;
    if (!audio) return;

    try {
      audio.pause();
      audio.currentTime = 0;
    } catch {
      // ignore
    }

    setPlayingReference(false);
  };

  const stopUserAudio = () => {
    const audio = userAudioRef.current;
    if (!audio) return;

    try {
      audio.pause();
      audio.currentTime = 0;
    } catch {
      // ignore
    }

    setPlayingUser(false);
  };

  const playReference = ({ fromStart = true, onEnded = null } = {}) => {
    const audio = referenceAudioRef.current;
    if (!audio || !activeSegment?.audio_url) {
      setError("This segment does not have playable reference audio.");
      return;
    }

    stopUserAudio();

    if (fromStart) audio.currentTime = 0;

    audio.onended = () => {
      setPlayingReference(false);
      if (typeof onEnded === "function") onEnded();
    };

    audio.onerror = () => {
      setPlayingReference(false);
      setError("Could not play the reference audio.");
    };

    setError("");
    setPlayingReference(true);

    audio.play().catch((playError) => {
      console.error("Reference playback failed:", playError);
      setPlayingReference(false);
      setError("Could not play the reference audio.");
    });
  };

  const playUser = () => {
    const audio = userAudioRef.current;

    if (!audio || !playableUserAudioUrl) {
      if (!isPreparingUserAudio) {
        setError("Record your version first.");
      }
      return;
    }

    setError((current) => (isTransientUserAudioError(current) ? "" : current));
    stopReferenceAudio();

    audio.currentTime = 0;
    audio.onended = () => setPlayingUser(false);
    audio.onerror = () => {
      setPlayingUser(false);
      setError("Could not play your recording.");
    };

    audio.onplaying = () => {
      setError((current) => (isTransientUserAudioError(current) ? "" : current));
      setPlayingUser(true);
    };

    setError("");
    setPlayingUser(true);

    audio.play().catch((playError) => {
      console.error("User playback failed:", playError);
      setPlayingUser(false);

      if (playableUserAudioUrl && !isPreparingUserAudio) {
        setError("Could not play your recording.");
      }
    });
  };

  const stopAllPlayback = () => {
    stopReferenceAudio();
    stopUserAudio();
  };

  const fetchFullRecording = async () => {
    if (!recordingId) return;

    setLoading(true);
    setError("");

    try {
      const res = await fetch(
        `${process.env.REACT_APP_USER_API_URL}/presentation/shared-recording/${encodeURIComponent(recordingId)}`,
        { method: "GET" },
      );

      if (!res.ok) throw new Error(`Shared recording ${res.status}`);

      const json = await res.json();
      const nextRecording = json.recording || json.item || json;

      setFullRecording(nextRecording);
      setSegments(sortSegments(nextRecording?.items || []));
      setActiveIndex(0);
    } catch (fetchError) {
      console.error("Could not fetch shared recording:", fetchError);
      setError("Could not load full exercise. Showing available preview.");
      setSegments(sortSegments(recording?.items || []));
    } finally {
      setLoading(false);
    }
  };

  const fetchNarratorIntonation = async (segment = activeSegment, key = activeKey) => {
    if (!recordingId || !segment) return;

    const itemId = segment.item_id || segment.attempt_id || segment.audio_id || segment.index;
    if (!itemId) return;

    try {
      const res = await fetch(
        `${process.env.REACT_APP_USER_API_URL}/presentation_intonation_analysis/shared/${encodeURIComponent(recordingId)}/${encodeURIComponent(String(itemId))}/narrator`,
        { method: "GET" },
      );

      if (!res.ok) throw new Error(`Narrator intonation ${res.status}`);

      const json = await res.json();

      setNarratorIntonationByItem((prev) => ({
        ...prev,
        [key]: json,
      }));
    } catch (fetchError) {
      console.error("Could not fetch narrator intonation:", fetchError);
    }
  };

  const uploadRecording = async (blob) => {
    if (!recordingId || !activeSegment) return;

    const itemId = activeSegment.item_id || activeSegment.attempt_id || activeSegment.audio_id || activeSegment.index;
    if (!itemId) return;

    const formData = new FormData();
    const extension = getRecordingExtension(blob?.type);
    const filename = `exercise-${recordingId}-${itemId}-${Date.now()}.${extension}`;
    formData.append("uploaded_file", blob, filename);
    formData.append("mode", mode);
    formData.append("text", activeSegment.text || "");
    formData.append("tz", Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC");

    setAnalysisLoading(true);
    setWorkflowPhase("analyzing");
    stopWorkflowProgress();
    setError("");
    setRecordingStatus("Uploading and analyzing your recording…");

    try {
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const res = await fetch(
        `${process.env.REACT_APP_USER_API_URL}/presentation_intonation_analysis/shared/${encodeURIComponent(recordingId)}/${encodeURIComponent(String(itemId))}/user`,
        {
          method: "POST",
          headers,
          body: formData,
        },
      );

      if (!res.ok) throw new Error(`User intonation ${res.status}`);

      const json = await res.json();

      setUserResultByItem((prev) => ({
        ...prev,
        [activeKey]: json,
      }));

      if (json?.narrator_intonation || json?.narrator_pronunciation) {
        setNarratorIntonationByItem((prev) => ({
          ...prev,
          [activeKey]: {
            narrator_intonation: json.narrator_intonation,
            narrator_pronunciation: json.narrator_pronunciation,
          },
        }));
      }

      setWorkflowPhase("ready");
      setError((current) => (isTransientUserAudioError(current) ? "" : current));
      setRecordingStatus("Your recording is ready.");
    } catch (uploadError) {
      console.error("Could not analyze user recording:", uploadError);
      setError("Could not analyze your recording.");
      setWorkflowPhase("idle");
      setRecordingStatus("");
    } finally {
      setAnalysisLoading(false);
    }
  };

  const stopRecording = () => {
    const recorder = mediaRecorderRef.current;

    if (!recorder) {
      setRecordingNow(false);
      setRecordingStatus("");
      return;
    }

    setWorkflowPhase("stopping");
    setRecordingStatus("Stopping recording…");
    stopWorkflowProgress();

    try {
      if (recorder.state !== "inactive") {
        recorder.stop();
      } else {
        setRecordingNow(false);
      }
    } catch (stopError) {
      console.error("Could not stop recorder:", stopError);
      setRecordingNow(false);
      setRecordingStatus("");
    }
  };

  const startRecording = async ({ autoStopWithReference = false, maxAutoStopMs = null, preparedStream = null } = {}) => {
    if (recordingNow) return;

    stopUserAudio();
    setError("");
    setWorkflowPhase("recording");
    setRecordingStatus("Recording…");

    if (maxAutoStopMs) {
      startWorkflowProgress(maxAutoStopMs);
    }

    try {
      const stream = isLiveMediaStream(preparedStream)
        ? preparedStream
        : await prepareMicrophoneStream();

      mediaStreamRef.current = stream;
      preparedStreamRef.current = null;
      chunksRef.current = [];

      const recorder = createRecorder(stream);
      const recorderMimeType = recorder.mimeType || getRecorderMimeType();

      mediaRecorderRef.current = recorder;

      recorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) chunksRef.current.push(event.data);
      };

      recorder.onerror = (event) => {
        console.error("MediaRecorder error:", event?.error || event);
        setError("Recording failed.");
      };

      recorder.onstop = () => {
        const blobType =
          recorder.mimeType ||
          recorderMimeType ||
          chunksRef.current?.[0]?.type ||
          "audio/webm";

        const blob = new Blob(chunksRef.current, { type: blobType });

        chunksRef.current = [];
        setRecordingNow(false);
        mediaRecorderRef.current = null;

        if (maxRecordingTimeoutRef.current) {
          clearTimeout(maxRecordingTimeoutRef.current);
          maxRecordingTimeoutRef.current = null;
        }

        stopMediaStream(stream);

        if (blob.size > 0) {
          setRecordingStatus("Uploading and analyzing recording…");
          uploadRecording(blob);
        } else {
          setRecordingStatus("");
          setWorkflowPhase("idle");
        }
      };

      recorder.start();
      setRecordingNow(true);

      if (autoStopWithReference) {
        const reference = referenceAudioRef.current;
        if (reference) {
          reference.onended = () => {
            setPlayingReference(false);
            scheduleWorkflowTimeout(() => stopRecording(), SHADOWING_TIMING.POST_AUDIO_BUFFER);
          };
        }
      }

      if (maxAutoStopMs) {
        maxRecordingTimeoutRef.current = setTimeout(() => {
          if (mediaRecorderRef.current?.state === "recording") {
            stopRecording();
          }
        }, maxAutoStopMs);
      }
    } catch (recordError) {
      handleMicrophoneFailure(recordError);
    }
  };

  const startShadowingWorkflow = async () => {
    clearWorkflowTimers();
    setError("");
    setCuePulseCount(0);
    setEchoingPulseCount(0);

    let preparedStream;

    try {
      preparedStream = await prepareMicrophoneStream();
      await unlockReferenceAudioForIOS();
    } catch (microphoneError) {
      handleMicrophoneFailure(microphoneError);
      return;
    }

    setWorkflowPhase("cue");
    setRecordingStatus("Get ready…");
    startWorkflowProgress(SHADOWING_TIMING.AUDIO_START_DELAY + referenceDurationMs + SHADOWING_TIMING.POST_AUDIO_BUFFER);

    scheduleWorkflowTimeout(() => setCuePulseCount(1), SHADOWING_TIMING.PULSE_1_TIME);
    scheduleWorkflowTimeout(() => setCuePulseCount(2), SHADOWING_TIMING.PULSE_2_TIME);
    scheduleWorkflowTimeout(() => setCuePulseCount(3), SHADOWING_TIMING.PULSE_3_TIME);

    const recordingStartTime = SHADOWING_TIMING.AUDIO_START_DELAY + SHADOWING_TIMING.RECORDING_START_OFFSET;

    scheduleWorkflowTimeout(() => {
      startRecording({
        autoStopWithReference: true,
        maxAutoStopMs: referenceDurationMs + SHADOWING_TIMING.POST_AUDIO_BUFFER + 1400,
        preparedStream,
      });

      const audioDelay = Math.max(0, SHADOWING_TIMING.AUDIO_START_DELAY - recordingStartTime);

      scheduleWorkflowTimeout(() => {
        playReference({ fromStart: true });
      }, audioDelay);
    }, Math.max(0, recordingStartTime));
  };

  const startEchoingCountdown = (preparedStream = null) => {
    clearWorkflowTimers();
    setWorkflowPhase("countdown");
    setRecordingStatus("Get ready…");
    setEchoingPulseCount(0);
    startWorkflowProgress(ECHOING_TIMING.PULSE_3_TIME + ECHOING_TIMING.CUE_CLEAR_TIME);

    scheduleWorkflowTimeout(() => setEchoingPulseCount(1), ECHOING_TIMING.PULSE_1_TIME);
    scheduleWorkflowTimeout(() => setEchoingPulseCount(2), ECHOING_TIMING.PULSE_2_TIME);
    scheduleWorkflowTimeout(() => setEchoingPulseCount(3), ECHOING_TIMING.PULSE_3_TIME);

    scheduleWorkflowTimeout(() => {
      startRecording({ maxAutoStopMs: maxRecordingMs, preparedStream });
    }, ECHOING_TIMING.PULSE_3_TIME + ECHOING_TIMING.CUE_CLEAR_TIME);
  };

  const startEchoingWorkflow = async () => {
    clearWorkflowTimers();
    setError("");
    setCuePulseCount(0);
    setEchoingPulseCount(0);

    let preparedStream;

    try {
      preparedStream = await prepareMicrophoneStream();
      await unlockReferenceAudioForIOS();
    } catch (microphoneError) {
      handleMicrophoneFailure(microphoneError);
      return;
    }

    setWorkflowPhase("playing");
    setRecordingStatus("Listen first…");
    startWorkflowProgress(referenceDurationMs);

    playReference({
      fromStart: true,
      onEnded: () => {
        setPlayingReference(false);
        startEchoingCountdown(preparedStream);
      },
    });
  };

  const runMode = async () => {
    if (recordingNow || workflowPhase === "recording") {
      stopRecording();
      return;
    }

    if (workflowPhase === "cue" || workflowPhase === "countdown" || workflowPhase === "playing") {
      resetWorkflowVisuals();
      stopReferenceAudio();
      stopMediaStream();
      setRecordingStatus("");
      return;
    }

    if (mode === "manual") {
      try {
        const preparedStream = await prepareMicrophoneStream();
        startRecording({ maxAutoStopMs: maxRecordingMs, preparedStream });
      } catch (microphoneError) {
        handleMicrophoneFailure(microphoneError);
      }

      return;
    }

    if (mode === "shadowing") {
      await startShadowingWorkflow();
      return;
    }

    if (mode === "echoing") {
      await startEchoingWorkflow();
    }
  };

  useEffect(() => {
    if (protectedUserAudioObjectUrlRef.current) {
      URL.revokeObjectURL(protectedUserAudioObjectUrlRef.current);
      protectedUserAudioObjectUrlRef.current = null;
    }

    setProtectedUserAudioUrl(null);

    if (!userAudioUrl) {
      setIsPreparingUserAudio(false);
      return;
    }

    if (!needsProtectedUserAudio) {
      setIsPreparingUserAudio(false);
      setError((current) => (isTransientUserAudioError(current) ? "" : current));
      return;
    }

    let cancelled = false;
    setIsPreparingUserAudio(true);

    fetchProtectedAudioBlobUrl(userAudioUrl)
      .then((blobUrl) => {
        if (cancelled) {
          if (blobUrl) URL.revokeObjectURL(blobUrl);
          return;
        }

        protectedUserAudioObjectUrlRef.current = blobUrl;
        setProtectedUserAudioUrl(blobUrl);
        setIsPreparingUserAudio(false);
        setError((current) => (isTransientUserAudioError(current) ? "" : current));

        if (userAudioRef.current) {
          try {
            userAudioRef.current.load();
          } catch {
            // ignore load refresh
          }
        }
      })
      .catch((audioError) => {
        console.error("Could not fetch protected user audio:", audioError);
        if (!cancelled) {
          setIsPreparingUserAudio(false);
          setError("Could not prepare your recording for playback.");
        }
      });

    return () => {
      cancelled = true;
    };
  }, [userAudioUrl, needsProtectedUserAudio]);

  useEffect(() => {
    fetchFullRecording();
    return () => {
      clearWorkflowTimers();
      stopAllPlayback();
      if (mediaRecorderRef.current?.state === "recording") {
        mediaRecorderRef.current.stop();
      }
      stopMediaStream();
      if (protectedUserAudioObjectUrlRef.current) {
        URL.revokeObjectURL(protectedUserAudioObjectUrlRef.current);
        protectedUserAudioObjectUrlRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recordingId]);

  useEffect(() => {
    stopAllPlayback();
    resetWorkflowVisuals();
    setRecordingStatus("");
    fetchNarratorIntonation(activeSegment, activeKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeKey]);

  return (
    <div className="fixed inset-0 z-[90] overflow-y-auto bg-slate-950/25 px-4 py-5 backdrop-blur-md">
      <div className="mx-auto flex min-h-[calc(100vh-2.5rem)] w-full max-w-7xl flex-col overflow-hidden rounded-[34px] border border-blue-200 bg-gradient-to-br from-slate-50 via-white to-blue-50/30 shadow-[0_34px_110px_rgba(15,23,42,0.25)] ring-1 ring-white/80">
        <audio
          ref={referenceAudioRef}
          src={resolveAudioUrl(activeSegment?.audio_url)}
          preload="metadata"
        />
        <audio
          ref={userAudioRef}
          src={playableUserAudioUrl || ""}
          preload="metadata"
          onCanPlay={() => {
            setError((current) => (isTransientUserAudioError(current) ? "" : current));
          }}
          onLoadedData={() => {
            setError((current) => (isTransientUserAudioError(current) ? "" : current));
          }}
        />

        <header className="shrink-0 border-b border-blue-100 bg-white/95 px-5 py-4 shadow-sm backdrop-blur-xl">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex min-w-0 items-center gap-3">
              <button
                type="button"
                onClick={onBack}
                className="inline-flex h-11 items-center justify-center rounded-2xl border border-slate-200 bg-white px-4 text-sm font-black text-slate-700 shadow-sm transition hover:bg-slate-50 focus:outline-none focus:ring-4 focus:ring-blue-100"
              >
                ← Back to community
              </button>

              <div className="min-w-0">
                <div className="text-xs font-black uppercase tracking-[0.18em] text-blue-600">
                  Audio exercise
                </div>
                <h1 className="truncate text-2xl font-black tracking-tight text-slate-950">
                  {fullRecordingOrInitial?.title || "Untitled recording"}
                </h1>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded-full border border-blue-200 bg-blue-50 px-3 py-1.5 text-xs font-black text-blue-700">
                {fullRecordingOrInitial?.language || "Unknown language"}
              </span>
              <span className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-black text-slate-700">
                {owner}
              </span>
              {nativeSpeaker && (
                <span className="rounded-full border border-green-200 bg-green-50 px-3 py-1.5 text-xs font-black text-green-700">
                  native speaker
                </span>
              )}
              <span className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-black text-slate-600">
                {segments.length || fullRecordingOrInitial?.item_count || 0} segment{(segments.length || fullRecordingOrInitial?.item_count || 0) === 1 ? "" : "s"}
              </span>

              {exerciseUrl && (
                <button
                  type="button"
                  onClick={() => window.open(exerciseUrl, "_blank", "noopener,noreferrer")}
                  className="inline-flex h-9 items-center justify-center gap-2 rounded-full border border-blue-200 bg-blue-50 px-3.5 text-xs font-black text-blue-700 shadow-sm transition hover:-translate-y-0.5 hover:border-blue-300 hover:bg-blue-100 hover:shadow-md focus:outline-none focus:ring-4 focus:ring-blue-100"
                  title={exerciseUrl}
                >
                  Open exercise page
                  <span aria-hidden="true">↗</span>
                </button>
              )}

              {exerciseUrl && (
                <button
                  type="button"
                  onClick={() => navigator.clipboard?.writeText(exerciseUrl)}
                  className="inline-flex h-9 items-center justify-center rounded-full border border-slate-200 bg-white px-3 text-xs font-black text-slate-600 shadow-sm transition hover:border-slate-300 hover:bg-slate-50 focus:outline-none focus:ring-4 focus:ring-blue-100"
                  title={exerciseUrl}
                >
                  Copy link
                </button>
              )}
            </div>
          </div>
        </header>

        <main className="grid min-h-0 flex-1 gap-6 px-5 py-6 lg:grid-cols-[320px_minmax(0,1fr)]">
          <aside className="space-y-5 lg:sticky lg:top-5 lg:self-start">
            <section className="overflow-hidden rounded-[30px] border border-blue-100 bg-white shadow-[0_24px_70px_-50px_rgba(37,99,235,0.72)] ring-1 ring-blue-50">
              <div className="bg-gradient-to-br from-white via-blue-50/70 to-cyan-50/40 px-5 py-5">
                <p className="text-xs font-black uppercase tracking-[0.18em] text-blue-600">Practice mode</p>
                <h2 className="mt-1 text-xl font-black text-slate-950">
                  {activeMode.label}
                </h2>
                <p className="mt-1 text-sm font-semibold text-slate-500">
                  Segment {segments.length ? activeIndex + 1 : 0}/{segments.length || 0}
                </p>
              </div>

              <div className="space-y-3 border-t border-blue-100 bg-white/90 p-3">
                {MODES.map((item) => {
                  const isActiveMode = mode === item.id;
                  const canSwitch = !recordingNow && workflowPhase !== "recording" && !analysisLoading;

                  return (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => {
                        if (!canSwitch) return;
                        resetWorkflowVisuals();
                        setRecordingStatus("");
                        setMode(item.id);
                      }}
                      disabled={!canSwitch}
                      className={cx(
                        "group w-full rounded-[24px] border p-3 text-left transition focus:outline-none focus:ring-4 focus:ring-blue-100 disabled:cursor-not-allowed disabled:opacity-60",
                        isActiveMode
                          ? item.id === "shadowing"
                            ? "border-amber-200 bg-amber-50/45 shadow-sm ring-2 ring-amber-100"
                            : item.id === "manual"
                              ? "border-green-200 bg-green-50/70 shadow-sm ring-2 ring-green-100"
                              : "border-blue-300 bg-blue-50 shadow-sm ring-2 ring-blue-100"
                          : "border-slate-200 bg-white hover:border-blue-200 hover:bg-blue-50/50"
                      )}
                    >
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-sm font-black text-slate-950">{item.label}</span>
                          {isActiveMode && workflowPhase !== "idle" && workflowPhase !== "ready" && (
                            <span className={cx(
                              "rounded-full border px-2 py-0.5 text-[11px] font-black",
                              workflowPhase === "recording"
                                ? "border-red-200 bg-white text-red-700"
                                : workflowPhase === "cue" || workflowPhase === "countdown"
                                  ? "border-amber-200 bg-white text-amber-700"
                                  : workflowPhase === "playing"
                                    ? "border-blue-200 bg-white text-blue-700"
                                    : workflowPhase === "analyzing"
                                      ? "border-blue-200 bg-white text-blue-700"
                                      : "border-slate-200 bg-white text-slate-500"
                            )}>
                              {workflowLabel}
                            </span>
                          )}
                        </div>

                        <p className="mt-1 text-xs font-semibold leading-5 text-slate-500">
                          {item.subtitle}
                        </p>

                        {isActiveMode && (isCueVisible || workflowPhase === "playing" || workflowPhase === "recording") && (
                          <div className="mt-3 flex items-center gap-3">
                            {workflowPhase === "recording" ? (
                              <span className="inline-flex h-9 items-center rounded-2xl border border-red-200 bg-white px-3 text-xs font-black text-red-700">
                                ● Speak now
                              </span>
                            ) : workflowPhase === "playing" ? (
                              <span className="inline-flex h-9 items-center rounded-2xl border border-blue-200 bg-white px-3 text-xs font-black text-blue-700">
                                Listen first
                              </span>
                            ) : (
                              <CueDots
                                count={cueCount}
                                active={isCueVisible}
                                tone={item.id === "shadowing" ? "amber" : "blue"}
                              />
                            )}
                          </div>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </section>

            <section className="rounded-[22px] border border-slate-200 bg-white px-4 py-3 shadow-sm">
              <div className="flex items-center justify-between gap-3">
                <p className="text-[11px] font-black uppercase tracking-[0.16em] text-blue-600">Progress</p>
                <span className="text-sm font-black text-slate-900">
                  {segments.length ? activeIndex + 1 : 0}/{segments.length || 0}
                </span>
              </div>
              <div className="mt-3 flex items-center gap-3">
                <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-slate-100 ring-1 ring-inset ring-blue-50">
                  <div
                    className="h-full rounded-full bg-gradient-to-r from-blue-500 to-cyan-400 transition-all duration-300"
                    style={{ width: `${progressPercent}%` }}
                  />
                </div>
                <span className="min-w-[44px] text-right text-xs font-black text-blue-700">
                  {Math.round(progressPercent)}%
                </span>
              </div>
            </section>

            {description && (
              <section className="rounded-[26px] border border-slate-200 bg-white p-4 shadow-sm">
                <p className="text-xs font-black uppercase tracking-[0.18em] text-blue-600">About</p>
                <p className="mt-2 whitespace-pre-wrap text-sm font-semibold leading-6 text-slate-600">
                  {description}
                </p>
              </section>
            )}
          </aside>

          <section className="min-w-0 space-y-5">
            <section className="relative overflow-hidden rounded-[34px] border border-blue-100 bg-white p-5 shadow-[0_24px_70px_-46px_rgba(37,99,235,0.72)] ring-1 ring-blue-50">
              <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-blue-500 via-cyan-400 to-indigo-500" />

              <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <p className="text-xs font-black uppercase tracking-[0.18em] text-blue-600">Listen, record, compare</p>
                  <h2 className="mt-1 text-2xl font-black tracking-tight text-slate-950">
                    {activeSegment ? formatSegmentLabel(activeSegment, activeIndex) : "No segment selected"}
                  </h2>
                </div>

                {loading && (
                  <span className="rounded-full border border-blue-200 bg-blue-50 px-3 py-1.5 text-xs font-black text-blue-700">
                    Loading full exercise…
                  </span>
                )}
              </div>

              <div className="rounded-[28px] border border-blue-100 bg-gradient-to-br from-blue-50 via-cyan-50/40 to-white px-5 py-8 shadow-inner sm:px-7">
                <p className="text-xl font-black leading-relaxed tracking-tight text-slate-950 sm:text-2xl">
                  {activeSegment?.text || "No text available for this segment."}
                </p>
              </div>

              <div className="mt-5 rounded-[30px] border border-slate-200 bg-white/95 p-4 shadow-sm">
                <div className="grid grid-cols-1 gap-4 xl:grid-cols-[auto_minmax(0,1fr)_auto] xl:items-center">
                  <button
                    type="button"
                    onClick={() => setActiveIndex((value) => Math.max(0, value - 1))}
                    disabled={isFirst || recordingNow || workflowPhase === "cue" || workflowPhase === "countdown" || workflowPhase === "playing"}
                    className="inline-flex h-12 items-center justify-center rounded-2xl border border-slate-200 bg-white px-5 text-sm font-black text-slate-700 shadow-sm transition hover:bg-slate-50 focus:outline-none focus:ring-4 focus:ring-blue-100 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-400 disabled:shadow-none"
                  >
                    ← Previous
                  </button>

                  <div className="flex min-w-0 flex-col items-center justify-center gap-3">
                    {isProcessingRecording ? (
                      <ProcessingActionCard />
                    ) : (
                      <button
                        type="button"
                        onClick={runMode}
                        disabled={primaryButtonConfig.disabled}
                        className={cx(
                          "relative inline-flex h-20 w-full max-w-[420px] items-center justify-center overflow-hidden rounded-[24px] px-8 text-white shadow-xl transition focus:outline-none focus:ring-4 disabled:cursor-not-allowed disabled:opacity-70 disabled:shadow-none",
                          primaryButtonConfig.tone === "red"
                            ? "bg-gradient-to-r from-red-500 to-pink-600 shadow-red-600/25 hover:from-red-600 hover:to-pink-700 focus:ring-red-100"
                            : primaryButtonConfig.tone === "amber"
                              ? "bg-gradient-to-r from-amber-400 to-orange-500 shadow-amber-500/18 hover:from-amber-500 hover:to-orange-600 focus:ring-amber-100"
                              : primaryButtonConfig.tone === "blue"
                                ? "bg-gradient-to-r from-blue-500 to-indigo-600 shadow-blue-600/25 hover:from-blue-600 hover:to-indigo-700 focus:ring-blue-100"
                                : primaryButtonConfig.tone === "slate"
                                  ? "bg-gradient-to-r from-slate-500 to-slate-600 shadow-slate-500/20 focus:ring-slate-100"
                                  : "bg-gradient-to-r from-green-500 to-emerald-600 shadow-green-600/25 hover:from-green-600 hover:to-emerald-700 focus:ring-green-100"
                        )}
                      >
                        <span className="absolute inset-0 bg-white/0 transition hover:bg-white/10" aria-hidden="true" />
                        <span className="relative flex items-center gap-4">
                          <PrimaryActionIcon
                            value={workflowProgressPercent}
                            tone={primaryButtonConfig.tone}
                            active={primaryProgressActive}
                            recording={primaryButtonRecording}
                            symbol={primaryButtonConfig.symbol}
                          />
                          <span className="text-left">
                            <span className="block text-xl font-black leading-6">
                              {primaryButtonConfig.text}
                            </span>
                            <span className="mt-1 block text-xs font-semibold text-white/90">
                              {primaryButtonSubtitle}
                            </span>
                          </span>
                        </span>
                      </button>
                    )}

                    <div className="flex flex-wrap items-center justify-center gap-2">
                      <div className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-2 shadow-sm">
                        <span className="text-[11px] font-black uppercase tracking-[0.12em] text-slate-400">
                          Reference
                        </span>
                        <button
                          type="button"
                          onClick={() => (playingReference ? stopReferenceAudio() : playReference())}
                          disabled={!activeSegment?.audio_url || recordingNow || workflowPhase === "cue" || workflowPhase === "countdown" || analysisLoading}
                          className="inline-flex h-8 min-w-[62px] items-center justify-center rounded-xl bg-slate-50 px-3 text-sm font-black text-slate-700 transition hover:bg-blue-50 hover:text-blue-700 focus:outline-none focus:ring-4 focus:ring-blue-100 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-400"
                        >
                          {playingReference ? "Stop" : "Play"}
                        </button>
                      </div>

                      {userAudioUrl && (
                        <button
                          type="button"
                          onClick={() => (playingUser ? stopUserAudio() : playUser())}
                          disabled={recordingNow || isPreparingUserAudio || !playableUserAudioUrl}
                          className="inline-flex h-11 min-w-[164px] items-center justify-center rounded-2xl border border-green-200 bg-green-50 px-4 text-sm font-black text-green-800 shadow-sm transition hover:bg-green-100 focus:outline-none focus:ring-4 focus:ring-green-100 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-400 disabled:shadow-none"
                        >
                          {isPreparingUserAudio ? "Preparing…" : playingUser ? "Stop yours" : "Play yours"}
                        </button>
                      )}
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => setActiveIndex((value) => Math.min(segments.length - 1, value + 1))}
                    disabled={isLast || !segments.length || recordingNow || workflowPhase === "cue" || workflowPhase === "countdown" || workflowPhase === "playing"}
                    className="inline-flex h-12 items-center justify-center rounded-2xl border border-slate-200 bg-white px-5 text-sm font-black text-slate-700 shadow-sm transition hover:bg-slate-50 focus:outline-none focus:ring-4 focus:ring-blue-100 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-400 disabled:shadow-none"
                  >
                    Next →
                  </button>
                </div>
              </div>

              {(error || (!analysisLoading && recordingStatus) || userResult || isPreparingUserAudio) && (
                <div className={cx(
                  "mt-4 rounded-2xl border px-4 py-3 text-sm font-semibold",
                  error
                    ? "border-amber-100 bg-amber-50 text-amber-700"
                    : userResult && !analysisLoading && !recordingNow
                      ? "border-green-100 bg-green-50 text-green-700"
                      : "border-blue-100 bg-blue-50 text-blue-700"
                )}>
                  {error || (isPreparingUserAudio ? "Preparing your recording for playback…" : userResult ? "Your recording is ready. Pitch analysis loaded." : recordingStatus)}
                </div>
              )}
            </section>

            <section className="mt-3 overflow-hidden rounded-[34px] border border-slate-200 bg-white shadow-[0_20px_60px_-46px_rgba(15,23,42,0.45)]">
              <div className="border-b border-slate-100 bg-gradient-to-r from-white via-slate-50 to-blue-50/40 px-5 py-4">
                <p className="text-xs font-black uppercase tracking-[0.18em] text-blue-600">Pitch comparison</p>
                <h2 className="mt-1 text-xl font-black text-slate-950">
                  Narrated reference and your narration
                </h2>
              </div>

              <div className="grid gap-4 p-5 xl:grid-cols-2">
                {narratorIntonation ? (
                  <PitchCurveAudioPanel
                    intonationOverride={narratorIntonation}
                    audioRef={referenceAudioRef}
                    title="Narrated reference"
                    subtitle="Pitch curve follows the reference playback."
                    lineColor="#2563eb"
                    fillColor="rgba(37,99,235,0.08)"
                    cursorColor="37,99,235"
                    compact
                  />
                ) : (
                  <div className="rounded-[24px] border border-dashed border-blue-200 bg-blue-50/40 px-5 py-8 text-center text-sm font-semibold text-blue-600">
                    Loading reference pitch…
                  </div>
                )}

                {userIntonation ? (
                  <PitchCurveAudioPanel
                    intonationOverride={userIntonation}
                    audioRef={userAudioRef}
                    title="Your narration"
                    subtitle="Pitch curve follows your playback."
                    lineColor="#16a34a"
                    fillColor="rgba(22,163,74,0.08)"
                    cursorColor="22,163,74"
                    compact
                  />
                ) : (
                  <div className="rounded-[24px] border border-dashed border-green-200 bg-green-50/40 px-5 py-8 text-center text-sm font-semibold text-green-700">
                    Record once to show your pitch curve.
                  </div>
                )}
              </div>
            </section>


            <section className="overflow-hidden rounded-[34px] border border-slate-200 bg-white shadow-[0_20px_60px_-46px_rgba(15,23,42,0.45)]">
              <div className="border-b border-slate-100 bg-gradient-to-r from-white via-slate-50 to-green-50/40 px-5 py-4">
                <p className="text-xs font-black uppercase tracking-[0.18em] text-green-600">AI assessment</p>
                <h2 className="mt-1 text-xl font-black text-slate-950">
                  Your word and phoneme accuracy
                </h2>
              </div>

              <div className="p-5">
                <AssessmentWordDots
                  text={activeSegment?.text}
                  assessment={userPronunciation}
                  title="You"
                  tone="green"
                />
              </div>
            </section>
          </section>
        </main>
      </div>
    </div>
  );
}